// src/components/item-builder/TrueFalseModal.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// فرم خالی برای حالت ایجاد
const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  correct_answer: "true",
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم بک‌اند → فرم (برای حالت ادیت)
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const layout = body.layout || "top-bottom";

  const scoring = item.scoring || body.scoring || {};
  const rawAnswerKey = (scoring.answerKey || "").toString().toLowerCase();
  const correct_answer = rawAnswerKey === "false" ? "false" : "true";

  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    correct_answer,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function TrueFalseModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  // وقتی مدال باز می‌شود / آیتم عوض می‌شود، فرم را مقداردهی کن
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("truefalse.toasts.stem_required"));
      return;
    }

    try {
      const answerKey =
        String(form.correct_answer).toLowerCase() === "true" ? "true" : "false";

      const payload = {
        kind: "true_false",
        explanation: {
          fa: form.explanation_fa || undefined,
          en: form.explanation_en || undefined,
        },
        stem: {
          fa: form.stem_fa || undefined,
          en: form.stem_en || undefined,
        },
        body: {
          layout: form.layout,
        },
        scoring: {
          mode: "exact",
          answerKey,
        },
        points: Number.isFinite(Number(form.points)) ? Number(form.points) : 1,
        status: form.status || "draft",
        tags: form.tags
          ? form.tags
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };

      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("truefalse.toasts.updated", "سؤال درست/غلط با موفقیت ویرایش شد")
          : t("truefalse.toasts.created", "سؤال درست/غلط با موفقیت ساخته شد")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("truefalse.stem_fa")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              placeholder={t("truefalse.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("truefalse.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t("truefalse.stem_en_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
        </div>

        {/* Layout Selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("truefalse.layout")}
          </label>
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {["top-bottom", "bottom-top", "right-left", "left-right"].map(
              (layout) => (
                <div key={layout} className="flex items-center">
                  <input
                    type="radio"
                    id={`layout-${layout}`}
                    name="layout"
                    value={layout}
                    checked={form.layout === layout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, layout: e.target.value }))
                    }
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <label
                    htmlFor={`layout-${layout}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {t(`truefalse.layout_${layout}`)}
                  </label>
                </div>
              )
            )}
          </div>
        </div>

        {/* Correct Answer */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t("truefalse.correct_answer")}
          </label>
          <select
            value={form.correct_answer}
            onChange={onChange}
            name="correct_answer"
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="true">{t("truefalse.true")}</option>
            <option value="false">{t("truefalse.false")}</option>
          </select>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("truefalse.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={onChange}
              name="points"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("truefalse.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={onChange}
              name="status"
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("truefalse.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={onChange}
              name="tags"
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update") : t("common.create")}
        </button>
      </form>
    </div>
  );
}
